<template>
  <!-- 补充上传弹窗：task 为 null 时关闭；确认后由父组件置空 task 触发重置 -->
  <div class="overlay" :class="{ show: !!task }" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">补充上传案件材料</div>
        <button class="icon-btn" @click="$emit('close')">
          <svg class="icon sm"><use href="#jp-x" /></svg>
        </button>
      </div>
      <div class="modal-body">
        <div class="material-summary">
          补充材料会继续归入「{{ task?.name }}」当前任务，补充后系统将重新解析并继续生成{{ task?.type }}。
        </div>
        <div class="upload-zone" @click="pickSuppFiles">
          <svg class="icon lg" style="margin: 0 auto 10px; color: #1768ff"><use href="#jp-upload" /></svg>
          <strong>点击选择补充材料</strong>
          <div style="font-size: 12px; margin-top: 7px; color: #8996a8">
            支持 DOC / DOCX / PDF / XLS / XLSX / JPG / PNG
          </div>
        </div>
        <div class="supplement-files">
          <div v-for="(f, i) in suppFiles" :key="f.name" class="supp-file">
            <div>
              <FileThumb :name="f.name" />
            </div>
            <div class="supp-file-main">
              <div class="supp-file-name">{{ f.name }}</div>
              <div class="supp-file-meta">{{ fileExt(f.name) }} · {{ f.size }} · 补充材料</div>
            </div>
            <button class="icon-btn" @click="suppFiles.splice(i, 1)">
              <svg class="icon sm"><use href="#jp-x" /></svg>
            </button>
          </div>
        </div>
        <input
          ref="suppFileInputRef"
          type="file"
          multiple
          accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
          hidden
          @change="handleSuppFiles"
        />
      </div>
      <div class="modal-actions">
        <button class="btn" @click="$emit('close')">取消</button>
        <button class="btn primary" :disabled="!suppFiles.length" @click="confirmSupplement">上传并重新解析</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { message } from "ant-design-vue";
import { ACCEPT_EXTS, fileExt, formatSize } from "../store";
import FileThumb from "./FileThumb.vue";

const props = defineProps({
  task: { type: Object, default: null },
});
const emit = defineEmits(["close", "confirm"]);

const suppFiles = ref([]);
const suppFileInputRef = ref(null);

// 每次打开（task 变化）重置已选文件与 input
watch(
  () => props.task,
  () => {
    suppFiles.value = [];
    if (suppFileInputRef.value) suppFileInputRef.value.value = "";
  },
);

function pickSuppFiles() {
  suppFileInputRef.value?.click();
}
function handleSuppFiles(e) {
  [...e.target.files].forEach((f) => {
    const ext = fileExt(f.name).toLowerCase();
    if (!ACCEPT_EXTS.includes(ext)) {
      message.warning(`不支持 ${f.name} 的文件格式`);
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      message.warning(`${f.name} 超过 50MB`);
      return;
    }
    if (!suppFiles.value.some((x) => x.name === f.name)) {
      // raw 保留原始 File 对象，确认时供 AddMaterial 接口上传
      suppFiles.value.push({ name: f.name, size: formatSize(f.size), raw: f });
    }
  });
  e.target.value = "";
}
function confirmSupplement() {
  if (!props.task || !suppFiles.value.length) return;
  emit("confirm", suppFiles.value.map((f) => ({ ...f })));
}
</script>

<style scoped>
.overlay {
  --brand: #1768ff;
  --brand-hover: #0e59db;
  --line-2: #f0f3f7;
}
.icon { width: 20px; height: 20px; stroke: currentColor; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.icon.sm { width: 16px; height: 16px; }
.icon.lg { width: 24px; height: 24px; }
button { cursor: pointer; }
.icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: #60718a;
  display: grid;
  place-items: center;
}
.icon-btn:hover { background: #eef4ff; color: var(--brand); }
.btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.btn:hover { background: #f8fbff; border-color: #c9d6e8; }
.btn.primary { background: var(--brand); border-color: var(--brand); color: #fff; }
.btn.primary:hover { background: var(--brand-hover); }
.btn:disabled {
  background: #f4f6f9 !important;
  border-color: #e1e6ee !important;
  color: #a1adbd !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}
.btn.primary:disabled { background: #a9c5f8 !important; border-color: #a9c5f8 !important; color: #fff !important; }

.overlay { position: fixed; inset: 0; background: rgba(18, 31, 53, 0.38); z-index: 100; display: none; align-items: center; justify-content: center; padding: 24px; }
.overlay.show { display: flex; }
.modal {
  width: min(680px, 92vw);
  max-height: 86vh;
  overflow: hidden;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 70px rgba(20, 36, 64, 0.25);
  border: 1px solid #e6ebf2;
  display: flex;
  flex-direction: column;
}
.modal-head { height: 58px; border-bottom: 1px solid var(--line-2); display: flex; align-items: center; justify-content: space-between; padding: 0 18px; flex: none; }
.modal-title { font-weight: 800; font-size: 17px; }
.modal-body { padding: 18px; overflow: auto; }
.modal-actions { padding: 14px 18px; border-top: 1px solid var(--line-2); display: flex; justify-content: flex-end; gap: 8px; flex: none; }
.material-summary { padding: 12px 13px; border-radius: 12px; background: #f7faff; border: 1px solid #dce7fb; color: #51657f; font-size: 12px; line-height: 1.65; }
.upload-zone { border: 1px dashed #b9cbe3; border-radius: 14px; background: #fbfcfe; padding: 24px; text-align: center; color: #65758d; margin-top: 12px; cursor: pointer; }
.upload-zone:hover { border-color: #83adf7; background: #f7faff; }
.supplement-files { display: grid; gap: 8px; margin-top: 12px; }
.supp-file { display: flex; align-items: center; gap: 10px; border: 1px solid #e6ecf5; border-radius: 10px; padding: 9px 10px; background: #fff; }
.supp-file-main { min-width: 0; flex: 1; }
.supp-file-name { font-size: 12px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.supp-file-meta { font-size: 11px; color: #8a97aa; margin-top: 3px; }
</style>
